import { ObjModel } from 'core/models';

/**
 * Archival API types.
 *
 * These mirror the endpoints as built in reportsv2-gopher (branch
 * feat/data-archival) - see SIERA-ARCHIVAL-API-CONTEXT.md §4/§5. The wire format
 * is camelCase, unlike the rest of this app's PascalCase APIs, so the casing here
 * is deliberate.
 */

// Status as the archival engine reports it. Mapped onto the app-wide ExportStatus
// for display - see mapArchivalStatus in archival.utility.
export enum ArchivalApiStatus {
  Pending = 'PENDING',
  Dispatched = 'DISPATCHED',
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

// Archival always targets old data, so a criteria resolves to an upper cutoff.
// `olderThan` ("3 months", "1 year") is resolved at dispatch time, the dates at
// write time.
export interface IArchivalCriteria {
  beforeDate?: string;
  olderThan?: string;
  afterDate?: string;
}

/**
 * The only entities the engine can archive - its EntityTableSet returns an empty
 * table set for anything else, so offering another type means the create call
 * comes back 400.
 *
 * Called `Entity` on the data-sources response and `Type` on NamespaceConfig;
 * same enum, same values.
 */
export type NamespaceType = 'Lead' | 'Activity' | 'Opportunity';

export interface IArchivalNamespaceSpec {
  namespace: string;
  // optional on the wire, but always sent: omitted, the backend guesses from the
  // namespace name and 400s when it cannot
  namespaceType?: NamespaceType;
  // narrows to specific entity type ids; omit or empty for the whole namespace
  entityIds?: Array<number>;
  // overrides the request-level criteria
  criteria?: IArchivalCriteria;
}

// POST dataapi/archival/create-request
export interface ICreateArchivalRequest {
  namespaces: Array<IArchivalNamespaceSpec>;
  criteria: IArchivalCriteria;
  enrichFromSource?: boolean;
}

// POST dataapi/archival/create-export - single namespace by design
export interface ICreateArchivalExport {
  namespace: string;
  // sent for parity with create-request; the export document has the field, but
  // ArchivalExportInput does not decode it yet, so it is dropped server-side today
  namespaceType?: NamespaceType;
  entityIds?: Array<number>;
  criteria: IArchivalCriteria;
  fileType: string;
}

// Response of both creates
export interface IArchivalQueuedResponse {
  id: string;
  status: ArchivalApiStatus;
  createdOn: string;
}

// Per-namespace audit trail the cron/worker append to a request
export interface IArchivalDispatchedJob {
  namespace: string;
  jobName: string;
  status?: ArchivalApiStatus;
  rowsCopied?: number;
  error?: string;
  completedOn?: string;
}

// GET dataapi/archival/get-requests
export interface IArchivalRequestRow {
  id: string;
  namespaces: Array<IArchivalNamespaceSpec>;
  criteria: IArchivalCriteria;
  enrichFromSource: boolean;
  status: ArchivalApiStatus;
  dispatched?: Array<IArchivalDispatchedJob>;
  // a user id here, unlike the export row's display name - known API defect, so
  // this is not rendered as-is
  requestedBy?: string;
  createdOn: string;
  modifiedOn: string;
  errorMessage?: string;
}

// GET dataapi/archival/get-exports
export interface IArchivalExportRow {
  id: string;
  namespace: string;
  entityIds?: Array<number>;
  criteria: IArchivalCriteria;
  fileType: string;
  status: ArchivalApiStatus;
  publicLink?: string;
  rowsExported?: number;
  // a display name here, unlike the request row's user id
  requestedBy?: string;
  createdOn: string;
  // null until the worker finishes
  completedOn: string | null;
  expiresOn: string | null;
  errorMessage?: string;
}

/**
 * GET dataapi/archival/get-record - the archived record lookup.
 *
 * The contract is agreed but the route is NOT implemented yet, so calls return
 * 404 and the Search Archive screen shows its empty state until it lands.
 *
 * Column names and types are the *physical* source columns catalogued in
 * Archived-Table-Metadata, not report display names. There is deliberately no
 * archivedOn: the copy is an INSERT … SELECT, so archived rows carry no archival
 * timestamp - do not add an "Archived On" column.
 */
export interface IArchivedColumn {
  name: string;
  dataType: string;
  columnType?: string;
}

export interface IArchivedRecord {
  namespace: string;
  namespaceType: NamespaceType;
  id: string;
  // which archive table the row came from, e.g. Prospect_Base
  table: string;
  // empty -> fall back to the keys of `record`
  columns: Array<IArchivedColumn>;
  record: ObjModel.Obj;
}

export interface IArchivalRecordQuery {
  namespace: string;
  namespaceType?: NamespaceType;
  // the source system's primary key: ProspectID for Lead,
  // ProspectActivityId for Activity/Opportunity
  id: string;
}

/**
 * Form-side only. The API has no `Mode` - the intent is carried by the shape of
 * the criteria that gets sent (see toCriteria in archival.utility).
 */
export enum ArchivalRequestMode {
  // keep everything from the chosen date onwards, archive what is older
  Keep = 'Keep',
  // archive the chosen window
  Archive = 'Archive',
}

// Namespace dropdown option. Comes from the hardcoded list in
// archival.constants - archival has no namespace endpoint.
export interface IArchivalNamespace {
  Namespace: string;
  DisplayName: string;
  // sent through as namespaceType on every write; the user never picks it
  Type: NamespaceType;
}
